import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListColumnOptionType, ListColumnPreferenceEntry, ListColumnType } from 'types/ListTypes';
import { getColumnOptionLabel } from './functions/columnPreferences';
import { useModal } from 'components/modals/ModalContext';
import SecondaryButton from 'components/buttons/SecondaryButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import CheckboxCheck from 'assets/CheckboxCheck.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleQuestion } from '@fortawesome/free-solid-svg-icons';
import '../../style/scss/tooltip.scss';
import '../../style/scss/forms.scss';
import '../../style/scss/list.scss';

/*
 * ListColumnsModal.tsx
 * Small modal opened from the list more menu in which the user
 * picks the visible columns of a list or resets the list back to
 * its default columns. Reordering happens by dragging the column
 * headers in the list itself. A user with the change settings
 * right can apply the chosen columns to every user of the company.
 */

interface ListColumnsModalProps {
    listKey: string;
    columnOptions: ListColumnOptionType[];
    defaultColumns: ListColumnType[];
    currentColumns: ListColumnPreferenceEntry[];
    onSave: (columns: ListColumnPreferenceEntry[] | null, applyToEnvironment: boolean) => void;
    canApplyToEnvironment?: boolean;
    viewKey?: string;
}

const ListColumnsModal: React.FC<ListColumnsModalProps> = ({
    listKey, columnOptions, defaultColumns, currentColumns, onSave, canApplyToEnvironment = false,
}) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();

    // Keep the chosen columns in their display order as local state
    const [selectedEntries, setSelectedEntries] = useState<ListColumnPreferenceEntry[]>(currentColumns);

    // Keep track of whether the columns are applied to every user of the company
    const [applyToEnvironment, setApplyToEnvironment] = useState<boolean>(false);

    // Reveal the modal on first render, there is no data to load
    useEffect(() => {
        revealModal();
    }, []);

    // Resolves the label of a column field from the defaults or the options
    const getFieldLabel = (field: string): string => {
        const defaultColumn = defaultColumns.find(column => column.field === field);
        if (defaultColumn) {
            return t(defaultColumn.label);
        }
        const option = columnOptions.find(columnOption => columnOption.name === field);
        if (!option) {
            return field;
        }

        // A custom field label is user text and is shown as is
        return option.type === 'custom_field' ? option.label : t(getColumnOptionLabel(listKey, option));
    };

    // Build the available fields which are not currently selected
    const availableFields = useMemo(() => {
        const selectedFieldNames = new Set(selectedEntries.map(entry => entry.field));

        // Hide the raw model fields which a default column already covers,
        // such as the client option next to the client name column
        const coveredBaseFields = new Set(
            defaultColumns.map(column => (column.orderField ?? column.field).split('__')[0])
        );

        // Combine the default column fields with the backend column options
        const defaultFieldNames = defaultColumns.map(column => column.field);
        const optionFieldNames = columnOptions
            .map(option => option.name)
            .filter(name => !coveredBaseFields.has(name));
        const allFieldNames = Array.from(new Set([...defaultFieldNames, ...optionFieldNames]));

        // Only keep the fields which are not selected, sorted on their label
        return allFieldNames
            .filter(field => !selectedFieldNames.has(field))
            .sort((a, b) => getFieldLabel(a).localeCompare(getFieldLabel(b)));
    }, [selectedEntries, defaultColumns, columnOptions]);

    // Append a field to the end of the selected columns
    const handleAddField = (field: string) => {
        setSelectedEntries(previous => [...previous, { field }]);
    };

    // Remove a field from the selected columns
    const handleRemoveField = (field: string) => {
        setSelectedEntries(previous => previous.filter(entry => entry.field !== field));
    };

    // Save the chosen columns and close the modal
    const handleSave = () => {
        onSave(selectedEntries, applyToEnvironment);
        closeModal();
    };

    // Reset the list back to its default columns and close the modal
    const handleReset = () => {
        onSave(null, applyToEnvironment);
        closeModal();
    };

    // Renders one checkbox row, labeled with the column label unless a label is given, with an optional tooltip
    const renderFieldRow = (field: string, checked: boolean, onToggle: () => void, label?: string, tooltipText?: string) => (
        <div className='list-columns-row' key={field}>
            <div className='checkbox-wrapper'>
                <input
                    type='checkbox'
                    id={`list-column-${field}`}
                    checked={checked}
                    onChange={onToggle}
                    className='hidden-checkbox'
                />
                <label htmlFor={`list-column-${field}`}
                       className={`custom-checkbox ${checked ? 'checked' : ''}`}>
                    {checked && (
                        <img src={CheckboxCheck} alt='CheckboxCheck' className='checkbox-check' />
                    )}
                </label>
            </div>
            <label htmlFor={`list-column-${field}`} className='list-columns-row-label'>
                {label ?? getFieldLabel(field)}
            </label>
            {tooltipText && (
                <span className='tooltip-icon'>
                    <FontAwesomeIcon icon={faCircleQuestion} />
                    <span className='tooltip tooltip-right'>{t(tooltipText)}</span>
                </span>
            )}
        </div>
    );

    return (
        <div className='list-columns-modal'>
            <h6>{t('lists.columns_modal.selected_columns_label')}</h6>
            <div className='list-columns-selected'>
                {selectedEntries.map(entry => renderFieldRow(entry.field, true, () => handleRemoveField(entry.field)))}
            </div>
            <h6>{t('lists.columns_modal.available_columns_label')}</h6>
            <div className='list-columns-available'>
                {availableFields.map(field => renderFieldRow(field, false, () => handleAddField(field)))}
            </div>
            {canApplyToEnvironment && (
                <div className='list-columns-apply-environment'>
                    {renderFieldRow(
                        'apply-environment',
                        applyToEnvironment,
                        () => setApplyToEnvironment(previous => !previous),
                        t('lists.columns_modal.apply_environment_label'),
                        'lists.columns_modal.apply_environment_helper'
                    )}
                </div>
            )}
            <div className='button-row'>
                <div className='buttons-left'>
                    <SecondaryButton
                        onClick={handleReset}
                        label='lists.columns_modal.default_columns_label'
                        tooltipText='lists.columns_modal.default_columns_tooltip'
                        size="small" />
                </div>
                <div className='buttons-right'>
                    <SecondaryButton
                        onClick={() => closeModal()}
                        label='general.cancel'
                        size="small" />
                    <PrimaryButton
                        onClick={handleSave}
                        label='general.save'
                        size="small" />
                </div>
            </div>
        </div>
    );
};

export default ListColumnsModal;
